<template>
  <v-container class="form-fill" max-width="720">
    <!-- Loading the definition. -->
    <div v-if="status === 'loading'" class="form-fill__state">
      <v-progress-circular indeterminate color="primary" />
      <span class="ml-3">Loading form…</span>
    </div>

    <!-- The form could not be loaded (unknown / not active). -->
    <v-alert
      v-else-if="status === 'load-error'"
      type="error"
      variant="tonal"
      class="mb-4"
    >
      {{ loadError }}
      <div class="mt-3">
        <v-btn variant="text" color="primary" class="text-none" @click="goToList">
          Back to forms
        </v-btn>
      </div>
    </v-alert>

    <!-- Success receipt (fire-and-forget: confirm, offer another). -->
    <div v-else-if="status === 'success'" class="form-fill__state">
      <v-alert type="success" variant="tonal" class="mb-4" role="status">
        <div class="text-h6 mb-1">Submitted — thanks.</div>
        <div>Your {{ form.title }} has been recorded.</div>
      </v-alert>
      <div class="d-flex ga-3">
        <v-btn color="primary" variant="flat" class="text-none" @click="restart">
          Submit another
        </v-btn>
        <v-btn variant="text" class="text-none" @click="goToList">Back to forms</v-btn>
      </div>
    </div>

    <!-- The form. -->
    <v-form v-else novalidate @submit.prevent="submit">
      <h1 class="form-fill__title">{{ form.title }}</h1>
      <p v-if="form.description" class="form-fill__intro">{{ form.description }}</p>
      <p class="form-fill__required-note">* Required</p>

      <v-alert
        v-if="status === 'error'"
        type="error"
        variant="tonal"
        class="mb-4"
        role="alert"
      >
        {{ errorText }}
      </v-alert>

      <QuestionField
        v-for="q in form.questions"
        :key="q.key"
        :question="q"
        :model-value="answers[q.key]"
        :error="errors[q.key] || ''"
        @update:model-value="setAnswer(q.key, $event)"
      />

      <v-btn
        type="submit"
        color="primary"
        variant="flat"
        size="large"
        class="text-none mt-2"
        :loading="status === 'submitting'"
        :disabled="status === 'submitting'"
      >
        Submit
      </v-btn>
    </v-form>
  </v-container>
</template>

<script>
// FormFill — fetches a form definition and lets an employee fill and submit it.
// The renderer is generic (QuestionField per question); validation is mirrored
// client-side for instant feedback, but the server is authoritative (its 422
// field errors are mapped back onto the fields).
import FormApi from '@/services/FormApi'
import QuestionField from '@/components/forms/QuestionField.vue'

// A per-attempt idempotency key so a retry-after-timeout never double-submits.
function newIdempotencyKey () {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID()
  }
  return `k-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

function emptyAnswerFor (question) {
  return question.type === 'multi_choice' ? [] : null
}

function isEmpty (question, value) {
  if (value === undefined || value === null) return true
  if (typeof value === 'string' && value.trim() === '') return true
  if (question.type === 'multi_choice' && Array.isArray(value) && value.length === 0) return true
  return false
}

export default {
  name: 'FormFill',
  components: { QuestionField },
  props: {
    slug: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      form: null,
      answers: {},
      errors: {},
      status: 'loading', // loading | load-error | idle | submitting | success | error
      loadError: '',
      errorText: 'Something went wrong submitting the form. Your answers are kept — please try again.',
      idempotencyKey: newIdempotencyKey(),
    }
  },
  created () {
    this.load()
  },
  watch: {
    // The route reuses this component across :slug changes (same route record),
    // so created() does not re-run. Reload the definition when the slug changes.
    slug () {
      this.load()
    },
  },
  methods: {
    async load () {
      this.status = 'loading'
      try {
        const res = await FormApi.get(this.slug)
        this.form = res.data.form
        this.resetAnswers()
        this.status = 'idle'
      } catch (err) {
        this.loadError = (err && err.response && err.response.status === 404)
          ? 'This form is no longer available.'
          : 'The form could not be loaded. Please try again.'
        this.status = 'load-error'
      }
    },
    resetAnswers () {
      const answers = {}
      for (const q of (this.form.questions || [])) {
        answers[q.key] = emptyAnswerFor(q)
      }
      this.answers = answers
      this.errors = {}
      this.idempotencyKey = newIdempotencyKey()
    },
    setAnswer (key, value) {
      // If a prior submit failed, an edit means this is no longer the same
      // attempt. Mint a fresh idempotency key so an edited resubmit is recorded
      // as a NEW submission instead of being deduped to the unedited original
      // (which would silently discard the correction). A pure retry — pressing
      // Submit again with no edits — keeps the key and stays dedup-protected.
      if (this.status === 'error') {
        this.idempotencyKey = newIdempotencyKey()
      }
      this.answers = { ...this.answers, [key]: value }
      if (this.errors[key]) {
        const next = { ...this.errors }
        delete next[key]
        this.errors = next
      }
    },
    // Client-side required check for instant feedback. The server does the full,
    // authoritative validation.
    validateRequired () {
      const errors = {}
      for (const q of (this.form.questions || [])) {
        if (q.required && isEmpty(q, this.answers[q.key])) {
          errors[q.key] = `${q.label} is required.`
        }
      }
      this.errors = errors
      return Object.keys(errors).length === 0
    },
    async submit () {
      // Explicit in-flight guard (not just the disabled button) so a double
      // trigger can never fire two requests.
      if (this.status === 'submitting') return
      if (!this.validateRequired()) {
        this.status = 'idle'
        return
      }
      this.status = 'submitting'
      try {
        await FormApi.submit(this.slug, {
          answers: this.answers,
          idempotencyKey: this.idempotencyKey,
        })
        this.status = 'success'
      } catch (err) {
        // Map server field validation (422) back onto the fields and tell the user
        // to fix them — not the generic "try again". Other failures keep the
        // generic retry message. Entered values are retained either way.
        const res = err && err.response
        if (res && res.status === 422 && res.data && res.data.fields) {
          this.errors = res.data.fields
          this.errorText = 'Please fix the highlighted fields and submit again.'
        } else {
          this.errorText = 'Something went wrong submitting the form. Your answers are kept — please try again.'
        }
        this.status = 'error'
      }
    },
    restart () {
      this.resetAnswers()
      this.status = 'idle'
    },
    goToList () {
      this.$router.push({ name: 'forms' })
    },
  },
}
</script>

<style scoped>
.form-fill {
  padding-top: 16px;
  padding-bottom: 48px;
}

.form-fill__state {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 24px 0;
}

.form-fill__title {
  font-size: 1.6rem;
  font-weight: 800;
  color: #41281b;
  margin-bottom: 8px;
}

.form-fill__intro {
  color: #4a4a4a;
  margin-bottom: 12px;
}

.form-fill__required-note {
  font-size: 0.85rem;
  color: #929292;
  margin-bottom: 16px;
}
</style>
